import type { JSX } from 'react';
import { useRef, useState } from 'react';
import { Text, UnstyledButton } from '@mantine/core';

/**
 * A place to drop files on - dragged from the browser's download bar or a
 * folder - or to click for the file picker. Only the given types are passed on.
 */
export function FileDrop({ accept, multiple = false, onFiles, label, hint, disabled = false }: {
  accept: string;
  multiple?: boolean;
  onFiles: (files: File[]) => void;
  label: string;
  hint?: string;
  disabled?: boolean;
}): JSX.Element {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const types = accept.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean);
  const fits = (f: File): boolean => types.length === 0 || types.some((t) => f.name.toLowerCase().endsWith(t));
  const take = (list: FileList | null): void => {
    const files = Array.from(list ?? []).filter(fits);
    if (files.length) onFiles(multiple ? files : files.slice(0, 1));
  };
  return (
    <UnstyledButton
      onClick={() => !disabled && input.current?.click()}
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (!disabled) take(e.dataTransfer.files); }}
      aria-disabled={disabled}
      style={{
        display: 'block', width: '100%', padding: 'var(--mantine-spacing-md)', textAlign: 'center', borderRadius: 'var(--mantine-radius-md)',
        border: `2px dashed ${over ? 'var(--mantine-color-brand-5)' : 'var(--mantine-color-default-border)'}`,
        background: over ? 'var(--mantine-color-default-hover)' : undefined, opacity: disabled ? 0.5 : 1, cursor: disabled ? 'default' : 'pointer',
      }}
    >
      <Text size="sm" fw={500}>{label}</Text>
      {hint && <Text size="xs" c="dimmed">{hint}</Text>}
      <input ref={input} type="file" accept={accept} multiple={multiple} hidden onChange={(e) => { take(e.currentTarget.files); e.currentTarget.value = ''; }} />
    </UnstyledButton>
  );
}
